import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const formatToolCall = (name, actionGroup) => {
  const combined = `${name} ${actionGroup}`.toLowerCase();
  if (combined.includes('web') || combined.includes('duck') || combined.includes('internet') || combined.includes('www')) {
    return { icon: '🌐', label: 'Searched web' };
  }
  if (combined.includes('archive') || combined.includes('rag') || combined.includes('vector') || combined.includes('postgres') || combined.includes('jazz') || combined.includes('search')) {
    return { icon: '🎷', label: 'Searched jazz archive' };
  }
  return { icon: '🔧', label: name };
};

const parseContent = (content) => {
  const parts = content.split(/\n?Sources:\s*\n?/i);
  return { mainText: parts[0].trim(), sourcesText: parts[1] || null };
};

const renderSources = (text) => {
  const urls = text.match(/https?:\/\/[^\s)>]+/g) || [];
  if (!urls.length) return null;
  return (
    <div className="MessageSources">
      <span className="MessageSourcesLabel">Sources</span>
      {urls.map((url, i) => {
        let display;
        try { display = new URL(url).hostname.replace('www.', ''); }
        catch { display = url; }
        return (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="MessageSourceLink">
            {display}
          </a>
        );
      })}
    </div>
  );
};

export const Message = ({ message, isLast, onRegenerate }) => {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';
  const isStreaming = !!message.streaming;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const { mainText, sourcesText } = isUser
    ? { mainText: message.content, sourcesText: null }
    : parseContent(message.content || '');

  return (
    <>
      <div className={`MessageRow ${isUser ? 'MessageRow-user' : 'MessageRow-bot'}`}>
        <div className={`MessageBubble ${isUser ? 'MessageBubble-user' : 'MessageBubble-bot'}`}>
          <span className="MessageRole">{isUser ? 'You' : '🎷 MCG Jazz'}</span>

          {/* Tool call steps — ChatGPT-style rows with query */}
          {!isUser && message.toolCalls?.length > 0 && (
            <div className="MessageToolCalls">
              {message.toolCalls.map((tool, i) => {
                const { icon, label } = formatToolCall(tool.name || '', tool.actionGroup || '');
                return (
                  <div key={i} className="MessageToolCall">
                    <span className="MessageToolCall-icon">{icon}</span>
                    <span className="MessageToolCall-label">{label}</span>
                    {tool.query && (
                      <>
                        <span className="MessageToolCall-sep">·</span>
                        <span className="MessageToolCall-query">"{tool.query}"</span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Message content */}
          {isUser ? (
            <p className="MessageText">{message.content}</p>
          ) : isStreaming && !message.content ? (
            <div className="TypingIndicator">
              <span className="TypingDot" />
              <span className="TypingDot" />
              <span className="TypingDot" />
            </div>
          ) : message.error && !message.content ? (
            <p className="MessageText MessageText-error">
              Failed to get a response. Please try again.
            </p>
          ) : (
            <>
              <div className="MessageMarkdown">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ node, children, ...props }) => (
                      <a
                        {...props}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="MessageLink"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {mainText}
                </ReactMarkdown>
              </div>
              {isStreaming && <span className="StreamingCursor">▋</span>}
              {!isStreaming && sourcesText && renderSources(sourcesText)}
            </>
          )}

          {/* Copy button — bot messages only, not while streaming */}
          {!isUser && !isStreaming && message.content && (
            <button
              className="MessageCopyBtn"
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              {copied ? '✓' : '⎘'}
            </button>
          )}
        </div>
      </div>

      {/* Regenerate button below last bot message */}
      {!isUser && isLast && !isStreaming && onRegenerate && (
        <div className="MessageActions">
          <button className="MessageRegenerateBtn" onClick={onRegenerate}>
            ↺ Regenerate
          </button>
        </div>
      )}
    </>
  );
};
